import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SltDataServiceRequestService } from '../../services/slt-data-service-request.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-slt-data-service-request',
    templateUrl: './slt-data-service-request.component.html',
    styleUrls: ['./slt-data-service-request.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SltDataServiceRequestComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  
  filterParams: any = {};
  
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  
  // Component specific properties
  modalTitle = 'Data Service Request';
  showFormLoader = true;
  requestJson: string = '';
  requestJsonFormatted: any = null;
  crudLoading = false;

  constructor(
    private sltDataServiceRequestService: SltDataServiceRequestService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit() {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.showFormLoader = false;
      this.init();
    } else {
      this.showFormLoader = true;
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.appService = { USER_SETTINGS: header.userSettingsData || {}, SERVER_CONFIG: header.serverConfigData || {} };
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0 && this.GENERAL_CONFIG) {
        this.getRequestJson();
      }
    } catch (e) {
      console.error('Error in SltDataServiceRequest init:', e);
      this.showFormLoader = false;
    }
  }

  getRequestJson() {
    if (!this.rowData.LOG_ID) {
      console.error('LOG_ID not found in rowData');
      this.showFormLoader = false;
      return;
    }

    this.showFormLoader = true;
    this.crudLoading = true;

    this.sltDataServiceRequestService.getRequestJson(
      this.GENERAL_CONFIG.base_url,
      this.rowData.LOG_ID
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        
        if (response.callSuccess === "1" && response.jsonObject && response.jsonObject.length > 0) {
          try {
            // Parse the REQUEST JSON string
            const requestData = safeJsonInput<any>(response.jsonObject[0].REQUEST, null, 'slt-data-service-request.parse1');
            
            // Format JSON for display with proper indentation
            this.requestJson = JSON.stringify(requestData, null, 2);
            this.requestJsonFormatted = requestData;
            
            this.showFormLoader = false;
            this.cdr.detectChanges();
          } catch (parseError) {
            console.error('Error parsing REQUEST JSON:', parseError);
            this._snackBar.open('Error parsing request data', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
            this.showFormLoader = false;
            this.cdr.detectChanges();
          }
        } else {
          this._snackBar.open(response.errorMessage || 'Error loading request data', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.showFormLoader = false;
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.crudLoading = false;
        this.showFormLoader = false;
        console.error('Error loading request JSON:', error);
        this._snackBar.open('Error occurred while loading request data', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }
}

